import React, { useState, useEffect } from 'react'
import {
  FormField,
  FormItem,
  FormMessage,
  Form,
  FormLabel,
  FormControl,
  FormDescription
} from '../../components/ui/form'
import { Card } from '../../components/ui/card'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  setPasswordSchema
} from './validators'
import { Input } from '../../components/ui/input'
import { Button } from '../../components/ui/button'
import { ClipLoader } from 'react-spinners'
import img from '../../pics/landImg.jpeg'
import { useCreateNewPassword, useVerifyOtp } from '../../hooks/auth'
import { useToast } from '../../components/ui/use-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSetPassword } from '../../migration-api/api-hooks/auth'

const SetPassword = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Extract email and otp from URL parameters
  const emailFromUrl = searchParams.get('email')
  const otpFromUrl = searchParams.get('otp')

  const { setPassword, isLoading } = useSetPassword({
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Success',
        description: 'Password set successfully, please login'
      })

      setTimeout(() => {
        navigate('/')
      }, 3000)
    },
    onError: error => {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error?.response?.data?.message || 'Failed to set password'
      })
    }
  })

  const form = useForm({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      email: '',
      password: '',
      otp: ''
    }
  })

  // Set email and otp from URL params when component mounts
  useEffect(() => {
    if (emailFromUrl) {
      form.setValue('email', emailFromUrl)
    }
    if (otpFromUrl) {
      form.setValue('otp', otpFromUrl)
    }

    // Validate that required params exist
    if (!emailFromUrl || !otpFromUrl) {
      toast({
        variant: 'destructive',
        title: 'Invalid Link',
        description: 'Missing required information. Please use the link from your email.'
      })
      setTimeout(() => {
        navigate('/')
      }, 3000)
    }
  }, [emailFromUrl, otpFromUrl, form, toast, navigate])

  function onSubmit (values) {
    // The email and otp are already in the form values from the useEffect
    setPassword(values)
  }

  return (
    <section className='w-full h-full'>
      <div className='pl-24 pt-10'>
        <img src='/sproxillogo.png' alt='' className='w-[120px]' />
      </div>

      <div className='flex flex-col'>
        <div className='w-full h-[80vh] flex flex-col gap-[10px] justify-center items-center'>
          <Card className='h-[fit-content] w-[350px] p-[20px] min-w-[230px]'>
            <h3>Set Password</h3>
            <br />
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className='space-y-8'
              >
                {/* Optionally display the email (read-only) */}
                {emailFromUrl && (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        value={emailFromUrl}
                        disabled
                        className='bg-muted'
                      />
                    </FormControl>
                  </FormItem>
                )}

                <FormField
                  control={form.control}
                  name='password'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>New Password</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Enter new password'
                          type='password'
                          isPassword
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>
                        Enter your new password.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type='submit' variant='' disabled={isLoading}>
                  {isLoading ? (
                    <span className='mx-[20px]'>
                      <ClipLoader color='primary' size={20} />
                    </span>
                  ) : (
                    <span>Set Password</span>
                  )}
                </Button>
              </form>
            </Form>
          </Card>
        </div>
      </div>
      <div className='w-full flex flex-col items-center'>
        <div className='text-primary text-[9px]'>
          Copyright &copy; {new Date().getFullYear()}
        </div>
        <br/>
        <div className='h-[5vh] flex items-end w-full justify-center'>
          <img src={img} alt='' className='md:w-[65%]' />
        </div>
      </div>
    </section>
  )
}

export default SetPassword